import {
    AreaField,
    CurrencyType,
    DatePickerField,
    InputField,
    ProviderCollectionPayment,
    renderAmount,
    SelectField,
    SupportRefundCollectionPaymentPayload,
    ToastContext,
} from '@enter/core';
import dateFns from 'date-fns';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext } from 'services';
import { RootStore } from 'stores';
import * as Yup from 'yup';
import * as S from './invoice-item-dialog.styles';

type InvoiceItemDialogBase = {
    onClose: () => void;
    onSubmit: () => void;
};

type PredefinedPaymentProps = InvoiceItemDialogBase & {
    paymentId: string;
};

type SelectablePaymentProps = InvoiceItemDialogBase & {
    collectionPayments: ProviderCollectionPayment[];
};

type InvoiceItemDialogProps = PredefinedPaymentProps | SelectablePaymentProps;

const schema = Yup.object().shape({
    refundDate: Yup.string().required('Refund date is required'),
    refundAmount: Yup.object().shape({
        amount: Yup.string().required('Refund amount is required'),
    }),
});

type RefundPayload = SupportRefundCollectionPaymentPayload & {
    paymentId?: string;
};

const initial: RefundPayload = {
    paymentId: undefined,
    refundAmount: {
        amount: '0',
        currency: CurrencyType.USD,
    },
    refundDate: '',
    memo: '',
};

export const CollectionRefundDialog: React.FunctionComponent<InvoiceItemDialogProps> = inject('rootStore')(
    observer(
        (props: InvoiceItemDialogProps): React.ReactElement => {
            const dialogContext = useContext(DialogContext);
            const toastContext = useContext(ToastContext);

            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }

            const { claimStore } = rootStore;
            const { onSubmit, onClose } = props;

            const handleCancel = (): void => {
                onClose?.();
            };

            const handleSubmit = async (
                refund: RefundPayload,
                { setSubmitting }: FormikActions<any>,
            ): Promise<void> => {
                setSubmitting(true);

                if (!refund.paymentId) {
                    dialogContext.showError(
                        new Error('We can`t handle refund for this payment. Please contact the support.'),
                    );

                    setSubmitting(false);

                    return;
                }

                try {
                    await claimStore.refundCollectionPayment(refund.paymentId, refund);
                    toastContext.notify?.('Collection payment was refunded for this claim.', 'right');
                    onSubmit();
                } catch (err) {
                    dialogContext.showError(err);
                } finally {
                    setSubmitting(false);
                    onClose?.();
                }
            };

            const isSelectableRefund = isSelectableRefundProps(props);

            const defaultPaymentId = isSelectableRefund ? _.first(props.collectionPayments)?.id : props.paymentId;

            return (
                <S.DialogBody>
                    <Formik
                        initialValues={{ ...initial, paymentId: defaultPaymentId }}
                        validationSchema={schema}
                        onSubmit={handleSubmit}>
                        {(formik: FormikProps<RefundPayload>): React.ReactElement => (
                            <>
                                {isSelectableRefund && (
                                    <S.InputRow>
                                        <S.InputName>
                                            <S.Label>Payment to refund</S.Label>
                                            <S.FormField
                                                name="paymentId"
                                                component={SelectField}
                                                required={true}
                                                options={props.collectionPayments.map(el => ({
                                                    label: getPaymentLabel(el),
                                                    value: el.id,
                                                }))}
                                            />
                                        </S.InputName>
                                    </S.InputRow>
                                )}
                                <S.InputRow>
                                    <S.InputName>
                                        <S.Label>Refund Date</S.Label>
                                        <S.FormField
                                            name="refundDate"
                                            component={DatePickerField}
                                            customDayPickerProps={{
                                                disabledDays: [
                                                    {
                                                        after: new Date(),
                                                    },
                                                ],
                                            }}
                                        />
                                    </S.InputName>
                                    <S.InputAmount>
                                        <S.Label>Amount</S.Label>
                                        <S.FormField
                                            name="refundAmount.amount"
                                            maxLength={100}
                                            component={InputField}
                                            type="number"
                                        />
                                    </S.InputAmount>
                                </S.InputRow>
                                <S.InputMemo>
                                    <S.Label>Notes</S.Label>
                                    <S.FormField name="memo" component={AreaField} />
                                </S.InputMemo>
                                <S.ButtonGroup
                                    changed={true}
                                    useNotify={false}
                                    onCancel={handleCancel}
                                    onSave={formik.submitForm}
                                    saveLabel="Save"
                                    saveNotice="Item added"
                                />
                            </>
                        )}
                    </Formik>
                </S.DialogBody>
            );
        },
    ),
);

const isSelectableRefundProps = (props: InvoiceItemDialogProps): props is SelectablePaymentProps => {
    return (props as SelectablePaymentProps).collectionPayments !== undefined;
};

const getPaymentLabel = (payment: ProviderCollectionPayment): string => {
    const collectedAmount = renderAmount(payment.collectedAmount);
    const refundedAmount = renderAmount(payment.refundedAmount);
    const created = dateFns.format(payment.paymentDate || '', 'MM/DD/YYYY');

    return `Collected: ${collectedAmount}, Refunded: ${refundedAmount} | ${created}`;
};
